import { ColourVariant } from "@shared/ui.design-system"
import React, { forwardRef } from "react"

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner"
import { Typography } from "../Typography/Typography"
import { TextVariants } from "../Typography/Typography.types"

import {
  buttonStyles,
  contentWrapperStyles,
  iconWrapperStyles,
  spinnerWrapperStyles,
} from "./Button.css"

export type ButtonVariantType = "primary" | "secondary" | "tertiary" | "link"
export type ButtonSize = "small" | "medium" | "large"
export type ButtonWidth = "default" | "minContent" | "full"

/**
 * Defines the props for the Button component.
 */
export type ButtonProps = {
  /**
   * The visual style of the button.
   * @default 'primary'
   */
  variant?: ButtonVariantType
  /**
   * The text content displayed inside the button. This is required.
   */
  text: string
  /**
   * The size of the button, affecting padding, height, and font size.
   * @default 'medium'
   */
  size?: ButtonSize
  /**
   * Defines the width behavior of the button.
   * 'default': an intrinsic width based on content and padding.
   * 'minContent': shrinks to the minimum possible width.
   * 'full': expands to the full width of its container.
   * @default 'default'
   */
  width?: ButtonWidth
  /**
   * The horizontal alignment of the button's content (text and icons).
   * @default 'center'
   */
  textAlign?: "left" | "center" | "right"
  /**
   * If `true`, displays a loading spinner and disables the button.
   * @default false
   */
  loading?: boolean
  /**
   * If `true`, disables the button and prevents user interaction.
   * @default false
   */
  disabled?: boolean
  /**
   * A descriptive label for accessibility, especially for icon-only buttons.
   * If not provided, it defaults to the `text` prop.
   */
  ariaLabel?: string
  /**
   * The native HTML `type` attribute for the button element.
   * @default 'button'
   */
  type?: "button" | "submit"
  /**
   * The ARIA role of the component.
   * @default 'button'
   */
  role?: "button" | "link"
  /**
   * A unique identifier for testing purposes (e.g., `data-testid`).
   */
  dataTestId?: string
  /**
   * Callback function to be executed when the button is clicked.
   */
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  /**
   * Callback function to be executed when the button receives focus.
   */
  onFocus?: (e?: React.FocusEvent<HTMLButtonElement>) => void
  /**
   * Callback function to be executed when the button loses focus.
   */
  onBlur?: (e?: React.FocusEvent<HTMLButtonElement>) => void
  /**
   * An icon element to display to the left of the text.
   */
  leftIcon?: React.ReactNode
  /**
   * An icon element to display to the right of the text.
   */
  rightIcon?: React.ReactNode
}
/**
 * A versatile and themeable button component that supports different variants, sizes,
 * and states, including loading and disabled.
 * It is built with accessibility and developer experience in mind.
 */

const getTypographyColour = (variant: ButtonVariantType): ColourVariant => {
  return variant === "primary" ? "secondary-soft" : "primary-dark"
}

const getFontSize = (
  variant: ButtonVariantType,
  size: ButtonSize
): TextVariants => {
  if (size === "small") return variant === "link" ? "link-xs" : "button-sm"
  if (size === "large") return "button-lg"
  return variant === "link" ? "link-md" : "button-md"
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      text,
      variant = "primary",
      size = "medium",
      width = "default",
      textAlign = "center",
      loading = false,
      disabled,
      ariaLabel,
      type = "button",
      role = "button",
      dataTestId,
      onClick,
      onFocus,
      onBlur,
      leftIcon,
      rightIcon,
    },
    ref
  ) => {
    const typographyColour = getTypographyColour(variant)
    const fontSize = getFontSize(variant, size)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || loading) {
        e.preventDefault()
        return
      }
      onClick?.(e)
    }

    return (
      <button
        ref={ref}
        type={type}
        role={role}
        disabled={disabled}
        aria-label={ariaLabel || text}
        data-testid={dataTestId}
        data-loading={loading}
        onClick={handleClick}
        onFocus={onFocus}
        onBlur={onBlur}
        className={buttonStyles({ variant, size, width, textAlign })}
      >
        {loading && (
          <div className={spinnerWrapperStyles}>
            <LoadingSpinner size="xs" colour={typographyColour} />
          </div>
        )}

        <div className={contentWrapperStyles}>
          {leftIcon && <span className={iconWrapperStyles}>{leftIcon}</span>}

          <Typography
            textAlign={textAlign}
            as="span"
            variant={fontSize}
            colour={typographyColour}
          >
            {text}
          </Typography>

          {rightIcon && <span className={iconWrapperStyles}>{rightIcon}</span>}
        </div>
      </button>
    )
  }
)

Button.displayName = "Button"
